import React, { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber, fmtPercent } from '../../shared/format';
import { BRAND_BLUE } from '../../shared/colors';
import { expandableRowProps } from '../../shared/expandableRow';
import {
  DimInsightCard,
  ExportMenu,
  INSIGHT_MIN_CLICKS,
  downloadCsv,
  downloadPdf,
  makeExport,
} from '../shared';

const COLORS = [BRAND_BLUE, '#6366f1', '#f59e0b'];
const ROW_COLORS = ['bg-[#32B1FF]', 'bg-indigo-500', 'bg-amber-500'];

// Export spec — one column list drives CSV + PDF (headers identical here). CSV emits
// raw values; PDF emits the formatted strings.
const DEVICE_EXPORT_COLUMNS = [
  { header: 'Device', get: (device) => device.device },
  { header: 'Clicks', get: (device) => device.clicks, pdf: (device) => device.clicks.toLocaleString() },
  { header: 'Cost', get: (device) => device.cost, pdf: (device) => fmtCurrency(device.cost) },
  { header: 'Conversions', get: (device) => device.conversions, pdf: (device) => fmtNumber(device.conversions, true) },
  { header: 'CPA', get: (device) => device.cpa ?? '', pdf: (device) => (device.cpa ? fmtCurrency(device.cpa) : '—') },
  { header: 'CTR', get: (device) => (device.ctr * 100).toFixed(2) + '%', pdf: (device) => fmtPercent(device.ctr) },
];
const DEVICE_EXPORT = makeExport(DEVICE_EXPORT_COLUMNS);

const DevicePerformance = ({ deviceData, loading, error, expanded, onToggle, rangeStart, rangeEnd }) => {
  const [openDevices, setOpenDevices] = useState({});
  const exportCsv = () => {
    if (!deviceData?.items?.length) return;
    downloadCsv(DEVICE_EXPORT.headers, deviceData.items.map(DEVICE_EXPORT.toCsvRow), `device-performance-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!deviceData?.items?.length) return;
    downloadPdf('Device Performance', DEVICE_EXPORT.pdfHeaders, deviceData.items.map(DEVICE_EXPORT.toPdfRow), `device-performance-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="5" y="2" width="14" height="16" rx="2" /><path d="M2 20h20" /><path d="M9 22h6" />
          </svg>
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Device Performance</span>
          <InfoTip text="Spend and conversions split by device — mobile, desktop, tablet. A heavy mobile audience with low conversions usually points to a landing-page mobile experience problem." />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{expanded ? 'Collapse' : 'Expand'}</span>
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t dark:border-gray-800">
          {error ? (
            <div className="pt-4"><ErrorState message={error} /></div>
          ) : loading ? (
            <div className="pt-4">
              <SkeletonChart height={240} />
            </div>
          ) : !deviceData?.items?.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No device data available for this period.</p>
          ) : (
            <>
              <div className="flex justify-end pt-3 pb-1">
                <ExportMenu onCsv={exportCsv} onPdf={exportPdf} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <div className="flex items-center justify-center">
                  {(() => {
                    const totalCost = deviceData.items.reduce((sum, device) => sum + device.cost, 0);
                    const pieData = deviceData.items.map((device) => ({
                      name: device.device.charAt(0) + device.device.slice(1).toLowerCase(),
                      value: device.cost,
                    }));
                    return (
                      <div className="relative w-52 h-52">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} dataKey="value" strokeWidth={0}>
                              {pieData.map((_, index) => (
                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <ThemedChartTooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const datum = payload[0];
                                return (
                                  <ThemedTooltipBox title={datum.name}>
                                    <div>{fmtCurrency(datum.value)}</div>
                                  </ThemedTooltipBox>
                                );
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Total Spend</span>
                          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtCurrency(totalCost)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <ThinScroll className="responsive-card-table-wrapper rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto">
                  <table className="responsive-card-table w-full text-xs min-w-[480px]">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                        <th className="text-left py-2 px-3 font-medium">Device</th>
                        <th className="text-right py-2 px-3 font-medium">Clicks</th>
                        <th className="text-right py-2 px-3 font-medium">Cost</th>
                        <th className="text-right py-2 px-3 font-medium">Conv.</th>
                        <th className="text-right py-2 px-3 font-medium">CPA</th>
                        <th className="text-right py-2 px-3 font-medium">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceData.items.map((device, index) => (
                        <tr
                          key={device.device}
                          {...expandableRowProps({
                            expanded: !!openDevices[device.device],
                            onToggle: () => setOpenDevices((prev) => ({ ...prev, [device.device]: !prev[device.device] })),
                            label: `metrics for ${device.device}`,
                            className: `border-b dark:border-gray-800 last:border-0 max-[499px]:cursor-pointer ${openDevices[device.device] ? '' : 'rc-collapsed'}`,
                          })}
                        >
                          <td data-label="Device" className={`rc-title-cell py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium ${openDevices[device.device] ? 'rc-title-open' : ''}`}>
                            <span className="inline-flex items-center gap-2">
                              <svg className={`hidden max-[499px]:block w-3 h-3 text-gray-400 transition-transform ${openDevices[device.device] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                              <span className={`inline-block w-2.5 h-2.5 rounded-full ${ROW_COLORS[index % ROW_COLORS.length]}`} />
                              {device.device.charAt(0) + device.device.slice(1).toLowerCase()}
                            </span>
                          </td>
                          <td data-label="Clicks" className="text-right px-3 text-gray-700 dark:text-gray-300">{device.clicks.toLocaleString()}</td>
                          <td data-label="Cost" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtCurrency(device.cost)}</td>
                          <td data-label="Conv." className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtNumber(device.conversions, true)}</td>
                          <td data-label="CPA" className="text-right px-3 text-gray-700 dark:text-gray-300">{device.cpa ? fmtCurrency(device.cpa) : '—'}</td>
                          <td data-label="CTR" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtPercent(device.ctr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </ThinScroll>
                  <DimInsightCard
                    insight={deviceData.insight}
                    totalClicks={deviceData.items.reduce((sum, device) => sum + device.clicks, 0)}
                    totalConv={deviceData.items.reduce((sum, device) => sum + device.conversions, 0)}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DevicePerformance;
